<template>
  <v-card :flat="flat" v-bind="$attrs" v-on="$listeners" :disabled="uploading" :loading="uploading">
    <v-card-text>
      <v-form ref="form" :disabled="disabled">
        <v-btn :disabled="disabled" class="text-none mr-4" outlined color="primary" @click="handleFileImport">
          <v-icon left>mdi-upload-outline</v-icon>
          Escolher arquivos
        </v-btn>
        <v-chip
          class="mr-2"
          v-for="(file, index) in selectedFiles"
          :key="file.name"
          outlined
          close
          @click:close="removeFile(index)"
        >
          {{ file.name }}
        </v-chip>
        <v-file-input
          ref="uploader"
          @change="onChange"
          small-chips
          :rules="uploadRules"
          show-size
          multiple
          hide-input
          flat
          prepend-icon=""
          :value="selectedFiles"
          :accept="accept"
        />
        <v-text-field
          v-model="documentDate"
          v-mask="['##/##/####']"
          label="Data de Referência"
          hint="A qual dia se referem os arquivos?"
          filled
          persistent-hint
        ></v-text-field>
        <v-text-field
          v-model="description"
          label="Descrição"
          hint="Breve descrição do conteúdo do arquivo"
          filled
          persistent-hint
        ></v-text-field>
      </v-form>
      <slot name="errors">
        <div v-if="errors.length > 0 && !hideErrors">
          <strong class="error--text">Atenção!</strong>
          <p class="mb-2">Um erro impediu o envio dos arquivos abaixo. Tente fazer o upload novamente.</p>
          <p class="mb-0">{{ errors.join(', ') }}</p>
        </div>
      </slot>
    </v-card-text>
    <v-card-actions class="pb-4 pr-4">
      <v-spacer />
      <v-btn :disabled="disabled" color="primary" class="text-none" @click="sendFiles">Enviar</v-btn>
    </v-card-actions>
    <slot name="uploaded">
      <v-card class="mt-2 overflow-y-auto" v-if="uploaded.length > 0 && !hideUploaded" elevation="0" max-height="240">
        <v-card-subtitle>Últimos arquivos enviados com sucesso:</v-card-subtitle>
        <v-card-text>
          <ul>
            <li v-for="(file, index) in uploaded" :key="`uploaded-${file.name}-${index}`">
              {{ file.name }}
            </li>
          </ul>
        </v-card-text>
      </v-card>
    </slot>
  </v-card>
</template>

<script>
import { MAX_DEFAULT_FILE_SIZE } from '@/constants/global'
import { mask } from 'vue-the-mask'

export default {
  props: {
    attendanceId: {
      type: Number,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    /**
     * Os tipos de arquivo aceitos.
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#accept
     */
    accept: String,
    /**
     * Número máximo de arquivos por upload
     */
    maxFiles: {
      type: Number,
      default: 10,
    },
    /**
     * Tamanho máximo de cada arquivo (em bytes)
     */
    maxFileSize: {
      type: Number,
      default: MAX_DEFAULT_FILE_SIZE,
    },
    /**
     * Array com os nomes dos arquivos que foram enviados com sucesso.
     * Pode ser usado com a opção .sync
     * Obs.: mantém o histórico enquanto o componente não for destruído.
     */
    uploaded: {
      type: Array,
      default: () => [],
    },
    /**
     * Array com os nomes dos arquivos que não foram enviados por conta
     * de algum erro no servidor.
     * Pode ser usado com a opção .sync.
     * Obs.: é zerado a cada novo envio.
     */
    errors: {
      type: Array,
      default: () => [],
    },
    /**
     * Indica se deve esconder a exibição de erros no próprio componente
     */
    hideErrors: Boolean,
    /**
     * Indica se deve esconder a exibição de arquivos enviados com sucesso
     * no próprio componente
     */
    hideUploaded: Boolean,
    flat: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  directives: {
    mask: {
      bind(el, binding, vnode, oldVnode) {
        if (binding.value) {
          mask(el, binding, vnode, oldVnode)
        }
      },
    },
  },
  computed: {
    formattedMaxSize() {
      return this.$util.toByteSize(this.maxFileSize)
    },
    uploadRules() {
      return [
        (v) => v.length > 0 || 'Forneça ao menos 1 arquivo para upload',
        (v) =>
          v.length <= this.maxFiles ||
          `Não é possível enviar mais de ${this.maxFiles} arquivo${this.maxFiles === 1 ? '' : 's'} por vez`,
        (v) =>
          v.length === 0 ||
          v.every((file) => file.size < this.maxFileSize) ||
          `O arquivo não pode exceder ${this.formattedMaxSize}`,
      ]
    },
  },
  data() {
    return {
      uploading: false,
      selectedFiles: [],
      documentDate: null,
      description: null,
    }
  },
  methods: {
    async sendFiles() {
      if (!this.$refs.form.validate()) {
        return
      }

      const successIndexes = new Set()
      const uploaded = []
      const errors = []

      try {
        this.resetErrors()
        this.uploading = true
        const data = {
          attendanceId: this.attendanceId,
          description: this.description,
          referenceDate: this.$util.toIsoDate(this.documentDate),
        }
        const promises = await Promise.allSettled(
          this.selectedFiles.map((file) => this.$api.uploadPatientAttendanceFile(this.uuid, file, data)),
        )

        promises.forEach((promise, index) => {
          if (promise.status === 'fulfilled') {
            uploaded.push(promise.value)
            successIndexes.add(index)
          } else {
            errors.push(this.selectedFiles[index].name + ': ' + promise.reason.toString().replace('Error: ', ''))
          }
        })
      } finally {
        this.$emit('send:finished', {
          errors: errors.length,
          uploaded: uploaded.length,
          total: this.selectedFiles.length,
        })
        this.$emit('update:uploaded', [...uploaded, ...this.uploaded])
        this.$emit('update:errors', errors)
        this.selectedFiles = this.selectedFiles.filter((_, index) => !successIndexes.has(index))
        this.$refs.form.resetValidation()
        this.uploading = false
      }
    },
    resetErrors() {
      this.$emit('update:errors', [])
    },
    onChange(event) {
      this.selectedFiles.push(...event)
      this.resetErrors()
    },
    handleFileImport() {
      this.$refs.uploader.$refs.input.click()
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1)
    },
  },
}
</script>

<style scoped></style>
